<template>
  <v-menu>
    <template v-slot:activator="{ on: menu, attrs }">
      <v-tooltip bottom color="white">
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            v-bind="attrs"
            v-on="{ ...tooltip, ...menu }"
            icon
            color="primary"
          >
            <v-icon>mdi-cloud-download-outline</v-icon>
          </v-btn>
        </template>
        <span class="black--text">Click to download generated reports</span>
      </v-tooltip>
    </template>
    <v-list>
      <v-list-item
        v-if="!activeCustomerView"
        :disabled="fileLoading"
        @click="() => handleDownloadFile(FileTypesMap.INTERNAL)"
      >
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          :width="2"
          v-if="fileLoading"
        />
        <v-list-item-title>internal.html</v-list-item-title>
      </v-list-item>
      <v-list-item
        :disabled="fileLoading"
        @click="() => handleDownloadFile(FileTypesMap.MATCH)"
      >
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          :width="2"
          v-if="fileLoading"
        />
        <v-list-item-title>match.html</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="!activeCustomerView"
        :disabled="fileLoading"
        @click="
          (event) =>
            handleGeneratedDownload(
              event,
              FileTypesMap.PREVIOUS_RUN,
              FileMimeTypesMap.EXCEL
            )
        "
      >
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          width="2"
          v-if="fileLoading"
        />
        <v-tooltip bottom color="white" v-else>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              color="primary"
              class="action-button caption"
              @click="
                (event) =>
                  handleGeneratedDownload(
                    event,
                    FileTypesMap.PREVIOUS_RUN_ADVANCED,
                    FileMimeTypesMap.EXCEL
                  )
              "
              v-on="on"
            >
              ADV
            </v-btn>
          </template>
          <span class="black--text">Download advanced previousRun.xlsx</span>
        </v-tooltip>
        <v-list-item-title>previousRun.xlsx</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="!activeCustomerView"
        :disabled="fileLoading"
        @click="() => handleDownloadFile(FileTypesMap.CORE)"
      >
        <v-btn
          icon
          color="primary"
          class="action-button"
          v-if="!fileLoading && hasSessionFiles"
          @click="handleUploadMappedCore"
        >
          <v-icon>mdi-cloud-upload-outline</v-icon>
        </v-btn>
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          :width="2"
          v-if="fileLoading || !enterpriseSessionId"
        />
        <v-list-item-title>mapped_core.csv</v-list-item-title>
      </v-list-item>
      <template v-if="!activeCustomerView">
        <v-list-item
          v-for="file in inputFiles"
          :key="file.id"
          :disabled="fileLoading"
          @click="handleDownloadInputFile(file)"
        >
          <v-progress-circular
            indeterminate
            class="mr-2"
            color="primary"
            size="20"
            :width="2"
            v-if="fileLoading"
          />
          <v-list-item-title>{{ file.name }}</v-list-item-title>
        </v-list-item>
      </template>
      <v-list-item
        v-if="!activeCustomerView"
        :disabled="fileLoading"
        @click="
          (event) =>
            handleGeneratedDownload(
              event,
              FileTypesMap.BATCH,
              FileMimeTypesMap.CSV
            )
        "
      >
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          :width="2"
          v-if="fileLoading"
        />
        <v-list-item-title>input_batch.csv</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="this.result.chewy_catalog"
        :disabled="fileLoading"
        @click="() => handleDownloadFile(FileTypesMap.CHEWY_CATALOG)"
      >
        <v-progress-circular
          indeterminate
          class="mr-2"
          color="primary"
          size="20"
          :width="2"
          v-if="fileLoading"
        />
        <v-list-item-title>chewy_catalog.csv</v-list-item-title>
      </v-list-item>
      <template v-if="canDownloadSessionFiles">
        <v-list-item
          v-for="sessionFile in SupportedSessionFiles"
          :key="sessionFile.name"
          :disabled="fileLoading"
          @click="
            () =>
              handleDownloadSessionFile(sessionFile.name, sessionFile.mimeType)
          "
        >
          <v-progress-circular
            indeterminate
            class="mr-2"
            color="primary"
            size="20"
            :width="2"
            v-if="fileLoading"
          />
          <v-list-item-title>{{ sessionFile.name }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
import _ from 'lodash';
import {
  FileTypesMap,
  FileMimeTypesMap,
  SupportedSessionFiles
} from '@/constants';
import { downloadBlobByLink } from '@/helpers';
import { tasksApiV1, filesApiV1 } from '@/api/v1';
import { filesApiV2, tasksApiV2 } from '@/api/v2';

const Component = {
  props: {
    result: {
      type: Object,
      default: () => ({})
    },
    activeCustomerView: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    FileTypesMap,
    FileMimeTypesMap,
    fileLoading: false,
    enterpriseSessionId: null,
    completedImportRequestId: null,
    inputFiles: [],
    SupportedSessionFiles
  }),
  watch: {
    result(value) {
      this.enterpriseSessionId = value.task.rhapsody_enterprise_session_id;
      this.completedImportRequestId =
        value.task.rhapsody_completed_import_request_id;
      this.getInputFiles();
    }
  },
  computed: {
    hasSessionFiles() {
      return Boolean(
        this.$store.getters.isMatcherApiV2Enabled
          ? this.completedImportRequestId
          : this.enterpriseSessionId
      );
    },
    canDownloadSessionFiles() {
      return this.hasSessionFiles && !this.activeCustomerView;
    }
  },
  methods: {
    getInputFiles() {
      this.inputFiles = _.filter(_.get(this.result, ['task', 'uploads'], []), {
        file_type: 'uploads'
      });
    },
    async handleDownloadInputFile(file) {
      const { key, name: fileName } = file;
      const fileExtension = fileName.split('.').pop();
      const mimeType =
        fileExtension === 'csv' ? FileMimeTypesMap.CSV : FileMimeTypesMap.EXCEL;

      this.fileLoading = true;

      const fileBlob = await (
        this.$store.getters.isMatcherApiV2Enabled ? filesApiV2 : filesApiV1
      ).getFile({
        mimeType,
        id: key,
        isExport: true
      });

      downloadBlobByLink(fileName, fileBlob, mimeType);

      this.fileLoading = false;
    },
    async handleDownloadFile(fileType) {
      const { id, name, mimeType } = this.getFileByType(fileType);

      this.fileLoading = true;

      const fileBlob = await (
        this.$store.getters.isMatcherApiV2Enabled ? filesApiV2 : filesApiV1
      ).getFile({
        id,
        type: fileType
      });

      downloadBlobByLink(name, fileBlob, mimeType);

      this.fileLoading = false;
    },
    async handleGeneratedDownload(event, fileType, mimeType) {
      event.stopPropagation();
      this.fileLoading = true;
      const { data, contentDisposition } = await (
        this.$store.getters.isMatcherApiV2Enabled ? filesApiV2 : filesApiV1
      ).getGeneratedFile(this.result.id, fileType);
      const fileName = contentDisposition.split('filename=').pop();

      downloadBlobByLink(fileName, data, mimeType);
      this.fileLoading = false;
    },
    async handleDownloadSessionFile(fileName, mimeType) {
      const {
        task: { id }
      } = this.result;

      if (id) {
        this.fileLoading = true;
        const { data, contentDisposition } = await (
          this.$store.getters.isMatcherApiV2Enabled ? filesApiV2 : filesApiV1
        ).getTaskSessionFile(id, fileName, mimeType);
        const fileNameDownload = contentDisposition.split('filename=').pop();

        downloadBlobByLink(fileNameDownload, data, mimeType);
        this.fileLoading = false;
      }
    },
    getFileByType(fileType) {
      switch (fileType) {
        case FileTypesMap.CORE:
          return {
            id: this.result.core_report,
            mimeType: FileMimeTypesMap.CSV,
            name: 'mapped_core.csv'
          };
        case FileTypesMap.MATCH:
          return {
            id: this.result.match_html,
            mimeType: FileMimeTypesMap.HTML,
            name: 'match.html'
          };
        case FileTypesMap.INTERNAL:
          return {
            id: this.result.internal_html,
            mimeType: FileMimeTypesMap.HTML,
            name: 'internal.html'
          };
        case FileTypesMap.CHEWY_CATALOG:
          return {
            id: this.result.chewy_catalog,
            mimeType: FileMimeTypesMap.CSV,
            name: 'chewy_catalog.csv'
          };
      }
    },
    async handleUploadMappedCore(event) {
      event.stopPropagation();
      const fileType = FileTypesMap.CORE;
      const { id: fileId } = this.getFileByType(fileType);
      const fileLoadingKey = `${fileType}Loading`;
      const {
        task: { id: taskId }
      } = this.result;

      this[fileLoadingKey] = true;

      await (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      ).uploadMappedCore(taskId, fileId);

      this[fileLoadingKey] = false;
      this.$notify({
        type: 'success',
        title: 'Upload finished',
        text: 'mapped_core.csv successfully uploaded'
      });
    }
  }
};

export default Component;
</script>

<style lang="scss">
.action-button {
  margin-left: -12px;
}
</style>
